import type {
  FormControlLabelProps,
  InputBaseComponentProps,
  SxProps,
  Theme,
} from "@mui/material";
import type { ColumnSort } from "@tanstack/react-table";
import type { MongoQuery } from "@ucast/mongo2js";
import type { CSSProperties } from "react";
import type { FieldError } from "react-hook-form";

import type {
  IDropdownItemExt,
  IRadioGroupItem,
  ISearchCriteria,
  ISelectListItem,
  ISelectListItemExt,
  RydeAppUnit,
  RydeUiUnit,
  TPermissionAction,
  TypeOrmMappedOperator,
} from ".";
import type {
  FORM_FIELD_VALUES,
  TGridFieldKeys,
} from "../constants/field-types-ids.list.const";
import type {
  DropdownAvailableIconsEnum,
  DropdownDataTransformationsFnsEnum,
  FieldTypeTarget,
  FontVariantEnum,
  PriceRateAdditionPricingType,
  TJsonTypeEnumValues,
  TopCalendarMode,
} from "../enums";
import type { DropdownResolverName } from "../helpers";
import type {
  IValidationsYupRoot,
  IValidationType,
} from "./dynamic-forms.validation.models";

export type EditFormTemplateType =
  | "address"
  | "checkbox"
  | "currency"
  | "date"
  | "date_range"
  | "datetime"
  | "datetime_range"
  | "dropdown"
  | "dropdown_bound"
  | "site_dropdown"
  | "email"
  | "group_title"
  | "html_info"
  | "id_label"
  | "map_bounds"
  | "map_polygon"
  | "multiselect"
  | "multiselect_bound"
  | "number"
  | "password"
  | "phone"
  | "radio_group"
  | "relation_counters"
  | "shift_template_cycle"
  | "switch"
  | "table_area_ratios"
  | "table_contacts"
  | "table_integrations"
  | "table_user_roles"
  | "table_time_schedule"
  | "tags"
  | "text"
  | "textarea"
  | "time"
  | "time_range"
  | "url"
  | "table_availability"
  | "passenger_address"
  | "hub_station_template"
  | "hub_station_standalone_template"
  | "json_viewer"
  | "color_picker"
  | "area_waypoints"
  | "boolean_image_icon"
  | "data_upload_run_summary"
  | "catch_a_ride_availability";

/** Grid cell templates: aligned with `FIELD_TYPE_IDS.GRID` keys. */
export type GridTemplateType = TGridFieldKeys;

export type PrimitiveType = number | string | boolean | null | undefined;

export interface IEditFormTabContainer {
  label?: string;
  fieldIds?: number[]; // custom field ids
}

export interface IFieldType {
  id: number;
  type: FieldTypeTarget;
  name?: EditFormTemplateType | GridTemplateType;
  description?: string;
  limitedTo?: (PrimitiveType | PrimitiveType[])[];
  validationTypes?: IValidationType[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes?: any;
}

export interface IDropdownItemFilter {
  ancestorFieldName: string;
  ownFieldName?: string;
  customActiveFieldName?: string;
  isIgnoreFilterUntilAncestorIsNotChosen?: boolean;
  filters?: Record<string, number | string | boolean>;
  /** Dropdown source to load the ancestor items from (used together with `matchAncestorItemArrayKey`). */
  ancestorItemsSource?: RydeUiUnit;
  /** Array property on the selected ancestor item; keep only options whose value is listed in it. */
  matchAncestorItemArrayKey?: string;
}

export enum InfluencedByResolverNameEnum {
  BOOKING_BOOK_TIME_BY_SHIFT = "BOOKING_BOOK_TIME_BY_SHIFT",
}

export interface IInfluencedBy {
  controlName: string;
  valueKey?: string;
  resolverName?: InfluencedByResolverNameEnum;
}

export enum DefaultTime {
  DAY_START = "DAY_START",
  DAY_END = "DAY_END",
}

export interface IFormAttributes {
  dropdownDataTransformationFn?: DropdownDataTransformationsFnsEnum;
  dropItemsSource?: RydeUiUnit;
  dropItemsSortingDirections?: "asc" | "desc";
  dropdownItemsSuffixOverride?: string;
  dropdownItemsResolverName?: DropdownResolverName;
  selectItems?: ISelectListItem[];
  radioGroupItems?: IRadioGroupItem[];
  // Value for add new record (if needed). A field whose jsonType is `array` or `object`
  // defaults to one too — the attribute is stored as jsonb, not as a scalar column.
  // Keep in sync with the backend `IFormAttributes`.
  newItemValue?: PrimitiveType | PrimitiveType[] | object;
  readonly?: boolean;
  disabled?: boolean;
  isCoordinate?: boolean;
  disableTrimming?: boolean;
  dropItemsDisableAdd?: boolean;
  hideDropdownEditButton?: boolean;
  intRounding?: boolean;
  excludeIds?: (string | number)[];
  dropdownItemsFilter?: IDropdownItemFilter;
  notBulkEditable?: boolean;
  noLabel?: boolean;
  influencedBy?: IInfluencedBy;
  disableCondition?: object;
  /** i18n key for an info toast shown when this field is cleared because its ancestor (e.g. shift) changed and it had a value. */
  clearedByAncestorMessageKey?: string;
  isNearbyButtonVisible?: boolean;
  nearbyStationDependency?: string;
  showFindAreaButton?: boolean;
  defaultTime?: DefaultTime;
  /** Date fields default to today when empty; set this to keep an untouched field empty and persist it as null. */
  allowEmptyDate?: boolean;
  disablePastDates?: boolean;
  excludeOnDuplicate?: boolean | object;
  maxItemsToAdd?: number;
  disallowOvernight?: boolean;
  leftOffsetLevel?: number;
  cssStyles?: CSSProperties;
  isFullWidth?: boolean;
  customWidth?: string; // not implemented now, to track place where it can be used for number template
  /** Fixed prefix rendered inside the input, e.g. a currency symbol on a money field. */
  startAdornment?: string;
  descriptionTranslationKey?: string;
  tooltipTranslationKey?: string;
  tooltipTranslationParams?: Record<string, any>;
  /** Relative help topic path, e.g. `systemparams/geoProdMaxShiftGapB` → `/helptopics/systemparams/geoProdMaxShiftGapB` */
  helpTopikUrl?: string;
  customContainerTopMargin?: number | string;
  boldValues?: (number | string)[];
  italicValues?: (number | string)[];
  optionIconMapping?: Map<string | number, DropdownAvailableIconsEnum>;
  disabledValues?: (number | string)[];
  highlightedIdsFrom?: string;
  /** i18n key for tooltip on chips/options whose id is in `highlightedIdsFrom`. */
  highlightedTooltipTranslationKey?: string;
  disableSorting?: boolean;
  filterDropdownCriteria?: ISearchCriteria<unknown>;
  validationDependsOn?: string[];
  translationPath?: string;
  fieldNameForm?: string;
  skipDropdownItemsTranslation?: boolean;
  customDropdownItemsFilter?: (
    dropDownItem: IDropdownItemExt | ISelectListItemExt,
  ) => boolean;
  // Changing it reloads the dropdown items from the cache, e.g. after another field created an item.
  dropdownItemsReloadKey?: string;
  onStationQuickAdded?: (stationId: number) => void;
  linkToAnotherEntityGridFilteredByCurrentParams?: {
    entityName: RydeAppUnit;
    calendarMode: TopCalendarMode;
    filterFieldId: number;
  };
  linkToEntityCard?: {
    entityName: RydeAppUnit;
    entityIdFromField: string;
  };
  dropDownTemplate?: string; // {{value}} {{label}} keys are supported, e.g. "{{value}} | {{label}}"
  isAddOnly?: boolean;
  checkDtoValuesForVisibleConditions?: boolean;
  checkSystemParamValuesForVisibleConditions?: boolean;
  disablePhoneCallable?: boolean;
  displayTextReplacedByFieldName?: string;
  originFieldName?: string;
  destinationFieldName?: string;
  directionFieldName?: string;
  // number input specific
  numberInput?: {
    minValue?: number;
    maxValue?: number;
  };
  // price rate specific
  priceRateAdditionPricingType?: PriceRateAdditionPricingType;
  switchExplicitFalseValue?: boolean;
  limitTagsNumber?: number;
  /** group_title: small | extra-small = reduced font size and muted color, extensible via .group-title--small / .group-title--extra-small */
  fontVariant?: FontVariantEnum;
  // generic upload specific
  allowCreateIfNotFound?: boolean;
}

export interface IGridAttributes {
  // used for csv_list template
  numberOfItemsToShow?: number;
  /** csv_list: mark hidden items with a trailing "..." instead of a "+N" count. */
  showHiddenItemsAsEllipsis?: boolean;
  fieldNameGrid?: string;
  whereFieldName?: string;
  whereSubFieldName?: string;
  whereOperator?: TypeOrmMappedOperator;
  gridTruncateChars?: number;
  gridColumnWidth?: number | string;
  isEditable?: boolean;
  disableCondition?: object;
  translationPath?: string;
  relateToEntity?: RydeAppUnit | "notification_dynamic";
  showId?: boolean;
  openEntityParams?: {
    openEntityName?: RydeAppUnit;
    // the path in row original for example: "propertyA.propertyB"
    openEntityNameRowOriginalPath?: string;
    displayValueRowOriginalPath?: string;
    openRecordIdRowOriginalPath: string;
  };
  csvListPath?: string;
  csvListMapFieldName?: string;
  phoneCallableByFieldName?: string;
  imageOn?: string;
  imageOff?: string;
}

export type TBooleanMultiselectFilter = {
  $in?: boolean[];
};

export interface IFilterAttributes {
  operatorBetween?: boolean;
  numberBetween: {
    defaultMin?: number;
    defaultMax?: number;
  };
}

export interface IMuiFormControl {
  name: string;
  formLabel?: string | null;
  labelTranslated?: string;
  formLabelColor?: string | null;
  formValidation?: IValidationsYupRoot | null;
  formAttributes?: IFormAttributes | null;
  e2eData?: string;
  size?: "small" | "medium";
  variant?: "outlined" | "filled";
  sx?: SxProps<Theme>;
  inputProps?: InputBaseComponentProps | undefined;
  labelPlacement?: FormControlLabelProps["labelPlacement"];
  tabIndex?: number;
}

export interface ICustomField extends IMuiFormControl {
  id?: number;
  uniqueAttrId?: string;
  entityId?: number;
  createdAt?: TDateISO | null;
  updatedAt?: TDateISO | null;
  deletedAt?: TDateISO | null;
  companyId?: number | null;

  isColumn?: boolean | string;
  jsonType?: TJsonTypeEnumValues;

  // form specific
  formFieldTypeId?: FORM_FIELD_VALUES | null;

  formVisibleCondition?: MongoQuery | null; // mongo-style json
  formIcon?: JSX.Element;
  // user profile datetime formats
  dateFormat?: string;
  timeFormat?: string;

  // grid specific
  gridFieldTypeId?: number; // IFieldType
  gridLabel?: string | null;
  gridGroupable?: boolean | string;
  gridSortable?: boolean | string;
  gridOrderable?: boolean | string; //disabled column order move
  gridAttributes?: IGridAttributes | null;
  gridVisibleCondition?: MongoQuery | null; // mongo-style json
  filterFieldTypeId?: number | null;
  filterSearchable?: boolean;
  filterAttributes?: IFilterAttributes | null;
  readPermissionId?: number | null;
  updatePermissionId?: number | null;
  readPermission?: IReadOrUpdatePermission | null;
  updatePermission?: IReadOrUpdatePermission | null;
}

export interface IGridSchema {
  columns?: number[]; //custom field ids
  // optional attributes to pre-set column conditions
  columnsOrder?: number[]; //custom field ids
  columnsVisibility?: Record<number, boolean>; //custom field ids
  groupBy?: GridGrouping[];
  sortBy?: ColumnSort[];
  defaultFilters?: number[];
}

export interface IFormSchema {
  layout?: IEditFormTabContainer[];
  companyId?: number;
  createdAt?: TDateISO | null;
  deletedAt?: TDateISO | null;
  entityId?: number;
  id?: number;
}

// main root scheme
export interface IDynamicEditFormRoot {
  formSchema?: IFormSchema;
  gridSchema?: IGridSchema;
  controls: ICustomField[];
}

export type FormErrorAdvanced = Record<
  string,
  Partial<{
    type: string | number;
    message: string;
  }>
>;

export type FormError =
  string | Partial<FieldError> | FormErrorAdvanced | null | undefined;

export type DynamicFilterVariant = "filterItem" | "filterAdd";

export type GroupingDirection = "asc" | "desc";

export class GridGrouping {
  id: number;
  desc?: boolean;
}

export class IReadOrUpdatePermission {
  id: number;
  entityId: number;
  action: TPermissionAction;
  description: string | null;
}

export interface IFilterDetail {
  control: ICustomField;
  filterId: string;
  filterValue: any;
}

export type TBulkFormPrefix = "bulk";
export type TBulkFormNameWithPrefix<T extends string> =
  `${T}_${TBulkFormPrefix}`;
